import { useState } from "react";
import { importApi } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Upload, FileSpreadsheet, FileText, FileImage, CloudDownload, CheckCircle2, AlertTriangle, Sparkles } from "lucide-react";
import { useNavigate } from "react-router-dom";

const FORMATS = [
  { icon: FileSpreadsheet, name: "Excel", exts: ".xlsx,.xls" },
  { icon: FileText, name: "Word", exts: ".docx" },
  { icon: FileImage, name: "PDF", exts: ".pdf" },
  { icon: FileText, name: "PageMaker", exts: ".pmd,.p65,.pm6,.pm7" },
  { icon: CloudDownload, name: "Google Drive", exts: "url" },
];

export default function ImportWizard() {
  const [step, setStep] = useState(1);
  const [file, setFile] = useState(null);
  const [driveUrl, setDriveUrl] = useState("");
  const [source, setSource] = useState("file"); // file | drive
  const [subjectDefault, setSubjectDefault] = useState("Physics");
  const [parsing, setParsing] = useState(false);
  const [parsed, setParsed] = useState(null);
  const [rows, setRows] = useState([]);
  const nav = useNavigate();

  const runParse = async () => {
    if (source === "file" && !file) return toast.error("Choose a file first");
    if (source === "drive" && !driveUrl) return toast.error("Enter a Google Drive URL");
    setParsing(true);
    try {
      const fd = new FormData();
      if (source === "file") fd.append("file", file);
      if (source === "drive") fd.append("drive_url", driveUrl);
      fd.append("subject_default", subjectDefault);
      const r = await importApi.parse(fd);
      setParsed(r);
      setRows(r.questions.map((q, i) => ({ ...q, _include: !q.duplicate })));
      setStep(3);
      if (r.count > 0) {
        toast.success(`Parsed ${r.count} question(s)${r.used_ai ? " (AI-assisted)" : ""}`);
      } else if (r.errors?.length) {
        toast.warning("No questions detected — see notes below");
      } else {
        toast.warning("Parsed but no questions found");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Parse failed");
    } finally { setParsing(false); }
  };

  const commit = async () => {
    const selected = rows.filter(r => r._include).map(({ _include, ...rest }) => rest);
    if (!selected.length) return toast.error("Nothing selected");
    try {
      const r = await importApi.commit(selected);
      toast.success(`Saved ${r.inserted} questions to bank`);
      nav("/admin/questions");
    } catch { toast.error("Save failed"); }
  };

  const updateRow = (i, patch) => setRows(prev => prev.map((r, idx) => idx === i ? { ...r, ...patch } : r));

  return (
    <div data-testid="import-wizard-page" className="space-y-6 max-w-6xl mx-auto">
      <div>
        <div className="text-xs font-bold uppercase tracking-[0.2em] text-primary">Import wizard</div>
        <h1 className="font-display font-bold text-3xl sm:text-4xl tracking-tight mt-1">Bring your question paper in.</h1>
        <p className="text-muted-foreground mt-1">Excel, Word, PDF, PageMaker, or a Google Drive link. AI parses. You review. Done.</p>
      </div>

      {/* stepper */}
      <div className="flex items-center gap-4">
        {["Upload", "Detect & parse", "Review & save"].map((s, i) => (
          <div key={s} className="flex items-center gap-2">
            <div data-testid={`step-${i+1}`} className={`h-8 w-8 rounded-full grid place-items-center text-sm font-semibold ${step >= i+1 ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"}`}>{i+1}</div>
            <span className={`text-sm ${step >= i+1 ? "text-foreground" : "text-muted-foreground"}`}>{s}</span>
            {i < 2 && <div className="w-8 h-px bg-border ml-2" />}
          </div>
        ))}
      </div>

      {step <= 2 && (
        <Card className="en-card p-6 space-y-6">
          <div>
            <Label className="text-sm font-semibold">Supported formats</Label>
            <div className="flex flex-wrap gap-2 mt-3">
              {FORMATS.map(f => (
                <div key={f.name} className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-muted text-sm">
                  <f.icon className="h-4 w-4 text-primary" /> {f.name}
                </div>
              ))}
            </div>
          </div>

          <Tabs value={source} onValueChange={setSource}>
            <TabsList data-testid="source-tabs" className="rounded-full">
              <TabsTrigger data-testid="tab-source-file" value="file" className="rounded-full">Upload file</TabsTrigger>
              <TabsTrigger data-testid="tab-source-drive" value="drive" className="rounded-full">Google Drive link</TabsTrigger>
            </TabsList>
            <TabsContent value="file" className="mt-4">
              <label htmlFor="file-input" className="block cursor-pointer border-2 border-dashed border-border rounded-2xl p-10 text-center hover:border-primary transition-colors duration-200">
                <Upload className="h-8 w-8 mx-auto text-muted-foreground" />
                <div className="mt-3 font-medium">{file ? file.name : "Click to choose a file"}</div>
                <div className="text-xs text-muted-foreground mt-1">Excel · Word · PDF · PageMaker</div>
                <input data-testid="file-input" id="file-input" type="file" className="hidden"
                  accept=".xlsx,.xls,.docx,.pdf,.pmd,.p65,.pm6,.pm7"
                  onChange={(e) => setFile(e.target.files?.[0] || null)} />
              </label>
            </TabsContent>
            <TabsContent value="drive" className="mt-4">
              <Label>Public Google Drive link</Label>
              <Input data-testid="drive-url-input" value={driveUrl} onChange={(e) => setDriveUrl(e.target.value)}
                placeholder="https://drive.google.com/file/d/…/view" className="mt-1" />
              <p className="text-xs text-muted-foreground mt-2">Make sure the link is set to "Anyone with the link can view".</p>
            </TabsContent>
          </Tabs>

          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label>Default subject (used when the file doesn't specify)</Label>
              <Select value={subjectDefault} onValueChange={setSubjectDefault}>
                <SelectTrigger data-testid="default-subject"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Physics">Physics</SelectItem>
                  <SelectItem value="Chemistry">Chemistry</SelectItem>
                  <SelectItem value="Mathematics">Mathematics</SelectItem>
                  <SelectItem value="Biology">Biology</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="flex justify-end">
            <Button data-testid="parse-btn" onClick={runParse} disabled={parsing} className="rounded-full px-8">
              <Sparkles className="h-4 w-4 mr-2" /> {parsing ? "Parsing…" : "Parse with AI"}
            </Button>
          </div>
        </Card>
      )}

      {step === 3 && parsed && (
        <Card className="en-card p-6 space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-display font-semibold text-lg">Review parsed questions</h3>
              <div className="text-sm text-muted-foreground flex flex-wrap items-center gap-2">
                <span>{parsed.count} found in <b>{parsed.filename}</b></span>
                {parsed.used_ai && <Badge variant="secondary" className="rounded-full">AI-assisted</Badge>}
              </div>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" className="rounded-full" onClick={() => { setStep(1); setParsed(null); }}>Start over</Button>
              <Button data-testid="commit-import" className="rounded-full" onClick={commit}><CheckCircle2 className="h-4 w-4 mr-2" /> Save selected</Button>
            </div>
          </div>

          {parsed.errors?.length > 0 && (
            <div data-testid="parse-warnings" className="p-3 rounded-xl bg-amber-500/10 border border-amber-500/30 text-amber-900 dark:text-amber-200 text-sm space-y-1">
              <div className="flex items-start gap-2">
                <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                <div className="space-y-1">
                  {parsed.errors.slice(0, 5).map((e, i) => <div key={i}>{e}</div>)}
                </div>
              </div>
            </div>
          )}

          <div className="space-y-3 max-h-[60vh] overflow-y-auto en-scroll">
            {rows.map((r, i) => (
              <div key={i} className={`p-4 rounded-xl border ${r._include ? "border-primary/40 bg-primary/5" : "border-border"}`}>
                <div className="flex items-start gap-3">
                  <input data-testid={`row-check-${i}`} type="checkbox" checked={!!r._include} onChange={(e) => updateRow(i, { _include: e.target.checked })} className="mt-1.5 h-4 w-4 accent-primary" />
                  <div className="flex-1 space-y-3">
                    <div className="flex flex-wrap gap-2">
                      <Badge variant="secondary" className="rounded-full">{r.type}</Badge>
                      <Badge variant="outline" className="rounded-full">{r.subject}</Badge>
                      <Badge variant="outline" className="rounded-full">{r.difficulty}</Badge>
                      {r.duplicate && <Badge className="rounded-full bg-amber-500/10 text-amber-700 border-amber-500/30" variant="outline">Duplicate</Badge>}
                    </div>
                    <Textarea value={r.text} onChange={(e) => updateRow(i, { text: e.target.value })} className="text-sm" />
                    {r.options?.length > 0 && (
                      <div className="grid sm:grid-cols-2 gap-2">
                        {r.options.map((opt, oi) => (
                          <Input key={oi} value={opt}
                            onChange={(e) => {
                              const opts = [...r.options]; opts[oi] = e.target.value; updateRow(i, { options: opts });
                            }} />
                        ))}
                      </div>
                    )}
                    <div className="flex flex-wrap gap-3 text-xs items-center">
                      <div><b>Correct:</b> {(r.correct || []).join(", ") || "—"}</div>
                      <div><b>Marks:</b> {r.marks}</div>
                      <div><b>-ve:</b> {r.negative_marks}</div>
                      {r.chapter && <div><b>Chapter:</b> {r.chapter}</div>}
                    </div>
                  </div>
                </div>
              </div>
            ))}
            {rows.length === 0 && <div className="text-center text-muted-foreground py-8">No questions parsed. Try another file or check the format.</div>}
          </div>
        </Card>
      )}
    </div>
  );
}
